import { Inject, Component, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { ValorizationItemAmount, ValorizationItem, ValorizationItemSupplie } from '../../../../model/valorization-item';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { MedicalAppointmentProcedure } from '../../../../model/medical-appointment-procedure';
import { RequiredCoverageValidator, RequiredAmountValidator } from '../../../../validators/valorization-edit.validator';
import { ValorizationService } from 'src/app/services/valorization/valorization.service';
import { AgreementPriceConfiguration } from 'src/app/model/agreement-price-configuration';
import { NotificationToastService } from '../../../../services/notification-toast.service';
import { AuthService } from '../../../../services/auth.service';

export interface ValorizationItemAmountEditDialogRequest {
  item: ValorizationItemAmount,
  noUpdateLikeParticular: boolean,
  noUpdateQuantity: boolean
}

@Component({
  selector: 'app-valorization-item-amount-edit-dialog',
  templateUrl: 'valorization-item-amount-edit-dialog.component.html',
})
export class ValorizationItemAmountEditDialogComponent implements OnInit {
  form: FormGroup;
  practiceRegionName: string;
  isSupplieEdition: boolean;
  canEditCosegure: boolean;
  canEditCoverage: boolean;
  canEditAmount: boolean;
  canUpdateLikeParticular: boolean;
  loading: boolean;

  constructor(public dialogRef: MatDialogRef<ValorizationItemAmountEditDialogComponent>
    , private formBuilder: FormBuilder
    , private notificationToastService: NotificationToastService
    , private valorizationService: ValorizationService
    , private authService: AuthService
    , @Inject(MAT_DIALOG_DATA) public data: ValorizationItemAmountEditDialogRequest) {
  }

  async ngOnInit() {
    await this.loadInfo();
  }

  private async loadInfo() {
    try {
      this.loading = true;
      if (!this.data.noUpdateLikeParticular) {
        this.canUpdateLikeParticular = this.authService.canUpdateLikeParticular();
      }
      const agreementPriceConfiguration =
        await this.valorizationService.getAgreementPriceConfiguration(this.data.item.ValorizationItem.AgreementPriceId);
      this.canEditCosegure = agreementPriceConfiguration.CanEditCosegure;
      this.canEditCoverage = agreementPriceConfiguration.CanEditCoverage;
      this.canEditAmount = agreementPriceConfiguration.CanEditAmount;
      this.isSupplieEdition = this.data.item.ValorizationItemSupplies &&
          this.data.item.ValorizationItemSupplies.length > 0;
      if (this.data.item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieName) {
        this.practiceRegionName = this.data.item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieName;
      } else if (this.data.item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionAditionalName) {
        this.practiceRegionName = this.data.item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionAditionalName;
      } else {
        this.practiceRegionName = this.data.item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionName;
      }
      this.form = this.formBuilder.group({
        RightsAmount: [''],
        FeeAmount: [''],
        ModuleAmount: [''],
        SupplieAmount: [''],
        RightsCoverage: [''],
        FeeCoverage: [''],
        SupplieCoverage: [''],
        ModuleCoverage: [''],
        RightsEntityAmount: [''],
        FeeEntityAmount: [''],
        ModuleEntityAmount: [''],
        SupplieEntityAmount: [''],
        PatientAmount: [''],
        EntityAmount: [''],
        TotalAmount: [''],
        PatientTaxAmount: [''],
        EntityTaxAmount: [''],
        TotalTaxAmount: [''],
        ValorizationItemSupplies: this.formBuilder.array([]),
        ValorizationItem: this.formBuilder.group({
          AmountDebited: [''],
          ContrastCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, false)]],
          DisposableCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, false)]],
          RightsCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, true)]],
          FeeCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, true)]],
          SupplieCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, true)]],
          ModuleCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, true)]],
          MedicamentCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, false)]],
          RadioactiveCoverage: [{ value: '', disabled: !this.isEditCoverageEnabled() },
          [RequiredCoverageValidator(this.isSupplieEdition, false)]],
          Id: [''],
          AssuranceAmount: [{ value: '', disabled: !this.isEditCosegureEnabled() }],
          RightsAmount: ['', RequiredAmountValidator(this.canEditAmount)],
          FeeAmount: ['', RequiredAmountValidator(this.canEditAmount)],
          SupplieAmount: ['', RequiredAmountValidator(this.canEditAmount)],
          ModuleAmount: ['', RequiredAmountValidator(this.canEditAmount)],
          ValorizationId: [''],
          AgreementPriceId: [''],
          PreInvoiceId: [''],
          TotalAmount: [''],
          EntityTotalAmount: [''],
          RightsEntityAmount: [''],
          FeeEntityAmount: [''],
          SupplieEntityAmount: [''],
          ModuleEntityAmount: [''],
          PreInvoiceDebitedId: [''],
          AssuranceAutoCalculate: [''],
          ManualAmount: [''],
          MedicalAppointmentProcedure: this.formBuilder.group({
            Id: [''],
            StudyReportId: [''],
            MedicalAppointmentProcedureCategoryId: [''],
            ExportDiagnosis: [''],
            PracticeRegionAditionalId: [''],
            PracticeRegionId: [''],
            SetSupplieId: [''],
            MedicalAppointmentId: [''],
            Quantity: ['', [Validators.required, Validators.min(0)]],
            MedicalAppointmentProcedureTypeId: [''],
            End: [''],
            Start: [''],
            InvoicingCenterId: [''],
            InvoicingDate: [''],
            InvoicingStateId: [''],
            PhysicianId: [''],
            NoInvoicingReasonId: [''],
            NoInvoicingReasonDescription: [''],
            OriginId: [''],
            AccessionNumber: [''],
            SetSupplieName: [''],
            PracticeRegionName: [''],
            PracticeRegionAditionalName: [''],
            CreateDate: [''],
            CreateUser: [''],
            UpdateDate: [''],
            UpdateUser: [''],
          })
        })
      });
      this.loadValorizationItemAmount(this.data.item);
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), 'Valorización');
    } finally {
      this.loading = false;
    }
  }

  get f() { return this.form.controls; }
  get fValorizationItem() { return this.valorizationItemFormGroup.controls; }
  get fMedicalAppointmentProcedure() { return this.medicalAppointmentProcedureFormGroup.controls; }
  get valorizationItemSuppiesFormArray() { return <FormArray>this.form.get('ValorizationItemSupplies'); }
  get valorizationItemFormGroup() { return <FormGroup>this.form.get('ValorizationItem'); }
  get medicalAppointmentProcedureFormGroup() { return <FormGroup>this.valorizationItemFormGroup.get('MedicalAppointmentProcedure'); }

  isEditCoverageEnabled(): boolean { return this.canEditCoverage; }
  isEditCosegureEnabled(): boolean { return this.canEditCosegure; }

  loadValorizationItemAmount(obj: ValorizationItemAmount): void {
    this.form.patchValue({
      RightsAmount: obj.RightsAmount,
      FeeAmount: obj.FeeAmount,
      ModuleAmount: obj.ModuleAmount,
      SupplieAmount: obj.SupplieAmount,
      RightsCoverage: obj.RightsCoverage,
      FeeCoverage: obj.FeeCoverage,
      SupplieCoverage: obj.SupplieCoverage,
      ModuleCoverage: obj.ModuleCoverage,
      RightsEntityAmount: obj.RightsEntityAmount,
      FeeEntityAmount: obj.FeeEntityAmount,
      ModuleEntityAmount: obj.ModuleEntityAmount,
      SupplieEntityAmount: obj.SupplieEntityAmount,
      PatientAmount: obj.PatientAmount,
      EntityAmount: obj.EntityAmount,
      TotalAmount: obj.TotalAmount,
      PatientTaxAmount: obj.PatientTaxAmount,
      EntityTaxAmount: obj.EntityTaxAmount,
      TotalTaxAmount: obj.TotalTaxAmount,
    });

    this.loadValorizacionItem(obj.ValorizationItem);
    if (obj.ValorizationItemSupplies) {
      this.loadValorizacionItemSupplies(obj.ValorizationItemSupplies);
    }
  }

  loadValorizacionItem(valorizacionItem: ValorizationItem) {

    this.valorizationItemFormGroup.patchValue({
      AmountDebited: valorizacionItem.AmountDebited,
      ContrastCoverage: valorizacionItem.ContrastCoverage,
      DisposableCoverage: valorizacionItem.DisposableCoverage,
      RightsCoverage: valorizacionItem.RightsCoverage,
      FeeCoverage: valorizacionItem.FeeCoverage,
      SupplieCoverage: valorizacionItem.SupplieCoverage,
      ModuleCoverage: valorizacionItem.ModuleCoverage,
      MedicamentCoverage: valorizacionItem.MedicamentCoverage,
      RadioactiveCoverage: valorizacionItem.RadioactiveCoverage,
      Id: valorizacionItem.Id,
      AssuranceAmount: valorizacionItem.AssuranceAmount,
      RightsAmount: valorizacionItem.RightsAmount,
      FeeAmount: valorizacionItem.FeeAmount,
      SupplieAmount: valorizacionItem.SupplieAmount,
      ModuleAmount: valorizacionItem.ModuleAmount,
      ValorizationId: valorizacionItem.ValorizationId,
      AgreementPriceId: valorizacionItem.AgreementPriceId,
      PreInvoiceId: valorizacionItem.PreInvoiceId,
      TotalAmount: valorizacionItem.TotalAmount,
      EntityTotalAmount: valorizacionItem.EntityTotalAmount,
      RightsEntityAmount: valorizacionItem.RightsEntityAmount,
      FeeEntityAmount: valorizacionItem.FeeEntityAmount,
      SupplieEntityAmount: valorizacionItem.SupplieEntityAmount,
      ModuleEntityAmount: valorizacionItem.ModuleEntityAmount,
      PreInvoiceDebitedId: valorizacionItem.PreInvoiceDebitedId,
      AssuranceAutoCalculate: valorizacionItem.AssuranceAutoCalculate,
      ManualAmount: valorizacionItem.ManualAmount
    });

    this.loadMedicalAppointmentProcedure(valorizacionItem.MedicalAppointmentProcedure);
  }

  loadMedicalAppointmentProcedure(medicalAppointmentProcedure: MedicalAppointmentProcedure) {
    this.medicalAppointmentProcedureFormGroup.patchValue({
      Id: medicalAppointmentProcedure.Id,
      ChangeBillingState: medicalAppointmentProcedure.ChangeBillingState,
      StudyReportId: medicalAppointmentProcedure.StudyReportId,
      MedicalAppointmentProcedureCategoryId: medicalAppointmentProcedure.MedicalAppointmentProcedureCategoryId,
      ExportDiagnosis: medicalAppointmentProcedure.ExportDiagnosis,
      PracticeRegionAditionalId: medicalAppointmentProcedure.PracticeRegionAditionalId,
      PracticeRegionId: medicalAppointmentProcedure.PracticeRegionId,
      SetSupplieId: medicalAppointmentProcedure.SetSupplieId,
      MedicalAppointmentId: medicalAppointmentProcedure.MedicalAppointmentId,
      Quantity: medicalAppointmentProcedure.Quantity,
      MedicalAppointmentProcedureTypeId: medicalAppointmentProcedure.MedicalAppointmentProcedureTypeId,
      End: medicalAppointmentProcedure.End,
      Start: medicalAppointmentProcedure.Start,
      InvoicingCenterId: medicalAppointmentProcedure.InvoicingCenterId,
      InvoicingDate: medicalAppointmentProcedure.InvoicingDate,
      InvoicingStateId: medicalAppointmentProcedure.InvoicingStateId,
      PhysicianId: medicalAppointmentProcedure.PhysicianId,
      NoInvoicingReasonId: medicalAppointmentProcedure.NoInvoicingReasonId,
      NoInvoicingReasonDescription: medicalAppointmentProcedure.NoInvoicingReasonDescription,
      OriginId: medicalAppointmentProcedure.OriginId,
      AccessionNumber: medicalAppointmentProcedure.AccessionNumber,
      SetSupplieName: medicalAppointmentProcedure.SetSupplieName,
      PracticeRegionName: medicalAppointmentProcedure.PracticeRegionName,
      PracticeRegionAditionalName: medicalAppointmentProcedure.PracticeRegionAditionalName,
      CreateDate: medicalAppointmentProcedure.CreateDate,
      CreateUser: medicalAppointmentProcedure.CreateUser,
      UpdateDate: medicalAppointmentProcedure.UpdateDate,
      UpdateUser: medicalAppointmentProcedure.UpdateUser,
    });
  }

  loadValorizacionItemSupplies(valorizationItemSuppplies: ValorizationItemSupplie[]) {
    valorizationItemSuppplies.forEach((item) => {
      const row = this.formBuilder.group({
        Id: [item.Id],
        Amount: [item.Amount],
        EntityAmount: [item.EntityAmount],
        PatientAmount: [item.PatientAmount],
        SupplieName: [item.SupplieName],
        SupplieId: [item.SupplieId],
        SupplieTypeId: [item.SupplieTypeId],
        ValorizationItemId: [item.ValorizationItemId]
      });
      this.valorizationItemSuppiesFormArray.push(row);
    });
  }

  saveLikeParticular(): void {
    if (this.form.valid) {
      this.dialogRef.close({
        toParticular: true,
        item: this.form.getRawValue()}
        );
    }
  }

  save(): void {
    if (this.form.valid) {
      this.dialogRef.close({
        toParticular: false,
        item: this.form.getRawValue()}
        );
    }
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
